import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { CATEGORIES, categoryColor } from "@/lib/categories";
import { formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ScanLine, Search, Trash2, FileSpreadsheet, Crown, Info } from "lucide-react";

export default function Receipts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, tc } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");

  const currentMonth = new Date().toISOString().slice(0, 7);
  const isThisMonth = (d) => (d || "").slice(0, 7) === currentMonth;

  const load = () => {
    setLoading(true);
    api.listReceipts().then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    return items.filter((r) => {
      const mOk = r.merchant?.toLowerCase().includes(search.toLowerCase());
      const cOk = cat === "all" || r.category === cat;
      return mOk && cOk;
    });
  }, [items, search, cat]);

  const total = filtered.reduce((s, r) => s + Number(r.total_amount || 0), 0);

  const del = async (id) => {
    try { await api.deleteReceipt(id); setItems((x) => x.filter((r) => r.id !== id)); toast.success(t("toast.receiptDeleted")); }
    catch { toast.error(t("toast.deleteError")); }
  };

  const exportCsv = async () => {
    if (!user?.is_premium) { toast.error(t("toast.csvPremium")); return; }
    try {
      const res = await fetch(api.exportCsvUrl(), { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "subzero_receipts.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("toast.csvExported"));
    } catch { toast.error(t("toast.exportFailed")); }
  };

  return (
    <div data-testid="receipts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">{t("receipts.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("receipts.summary", { count: items.length, total: formatMoney(total) })}</p>
          <p className="text-muted-foreground/80 text-xs mt-1 flex items-start gap-1.5 max-w-xl">
            <Info style={{ width: 13, height: 13, marginTop: 2, flexShrink: 0 }} /> {t("receipts.oldNote")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-csv-button" onClick={exportCsv} variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10">
            {user?.is_premium ? <FileSpreadsheet style={{ width: 16, height: 16 }} className="mr-2" /> : <Crown style={{ width: 16, height: 16 }} className="mr-2 text-cyan-300" />} {t("receipts.exportCsv")}
          </Button>
          <Button data-testid="receipts-scan-button" onClick={() => navigate("/scan")} className="bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">
            <ScanLine style={{ width: 16, height: 16 }} className="mr-2" /> {t("receipts.scan")}
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" style={{ width: 16, height: 16 }} />
          <Input data-testid="receipts-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("receipts.searchPlaceholder")} className="pl-9 bg-white/5 border-white/10" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="receipts-category-filter" className="sm:w-48 bg-white/5 border-white/10"><SelectValue placeholder={t("field.category")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("receipts.allCategories")}</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{tc(c)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-2">{[0,1,2,3].map((i) => <Skeleton key={i} className="h-16 bg-white/5" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="card-base p-12 text-center">
          <ScanLine className="text-muted-foreground mx-auto mb-3" style={{ width: 28, height: 28 }} />
          <p className="font-display font-semibold">{items.length === 0 ? t("receipts.noneTitle") : t("receipts.noMatch")}</p>
          <p className="text-sm text-muted-foreground mt-1">{items.length === 0 ? t("receipts.noneDesc") : t("receipts.noMatchDesc")}</p>
          {items.length === 0 && <Button onClick={() => navigate("/scan")} className="mt-4 bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">{t("receipts.scanAreceipt")}</Button>}
        </div>
      ) : (
        <div className="card-base overflow-hidden">
          <div className="hidden md:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground bg-white/4 border-b border-white/10">
            <div className="col-span-4">{t("receipts.colMerchant")}</div>
            <div className="col-span-3">{t("receipts.colCategory")}</div>
            <div className="col-span-2">{t("receipts.colDate")}</div>
            <div className="col-span-2 text-right">{t("receipts.colAmount")}</div>
            <div className="col-span-1"></div>
          </div>
          <div className="divide-y divide-white/8">
            {filtered.map((r) => (
              <div key={r.id} data-testid="receipt-row" className="grid grid-cols-12 items-center px-4 py-3 hover:bg-white/4 transition-colors">
                <div className="col-span-6 md:col-span-4 flex items-center gap-3 min-w-0">
                  {r.image_data ? <img src={r.image_data} alt="" className="h-9 w-9 rounded object-cover border border-white/10 shrink-0" /> : <span className="h-9 w-9 rounded bg-white/5 shrink-0" />}
                  <span className="truncate font-medium text-sm">{r.merchant}</span>
                </div>
                <div className="hidden md:block col-span-3">
                  <span className="inline-flex items-center gap-2 text-xs rounded-full px-2.5 py-1 border" style={{ borderColor: `${categoryColor(r.category)}40`, color: categoryColor(r.category), background: `${categoryColor(r.category)}10` }}>
                    <span className="h-2 w-2 rounded-full" style={{ background: categoryColor(r.category) }} />{tc(r.category)}
                  </span>
                </div>
                <div className="hidden md:block col-span-2 text-sm text-muted-foreground">
                  {formatDate(r.date)}
                  {r.date && !isThisMonth(r.date) && (
                    <span className="ml-2 inline-block text-[10px] rounded px-1.5 py-0.5 bg-white/5 text-muted-foreground/80 border border-white/10 align-middle">{t("receipts.notThisMonth")}</span>
                  )}
                </div>
                <div className="col-span-4 md:col-span-2 text-right font-mono tabular-nums text-sm">{formatMoney(r.total_amount, r.currency)}</div>
                <div className="col-span-2 md:col-span-1 flex justify-end">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid="receipt-delete-button" aria-label={t("common.delete")} className="text-muted-foreground hover:text-rose-300 p-2"><Trash2 style={{ width: 16, height: 16 }} /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-surface-1 border-white/10">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("receipts.deleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("receipts.deleteDesc", { merchant: r.merchant, amount: formatMoney(r.total_amount, r.currency) })}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-white/5 border-white/10">{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid="confirm-delete-receipt" onClick={() => del(r.id)} className="bg-rose-500 hover:bg-rose-400 text-white">{t("common.delete")}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
